import type { UseCaseArtworkId } from "@replohq/lattice/blocks/workflow-art/useCaseArtwork";
import type { Workflow } from "schemas/workflows";

import * as React from "react";

import { GLASS_PANEL_CLASS } from "@replohq/lattice/blocks/get-started-card/glassSurface";
import { UseCaseWorkflowArt } from "@replohq/lattice/blocks/workflow-art/UseCaseWorkflowArt";
import { cn } from "@replohq/lattice/utils/cn";

import softWaves from "../../../../../replo-agent/src/agent-ui/assets/activation/wallpaper-soft-waves.png";

import "@replohq/lattice/blocks/workflow-art/workflowArt.css";

export function WorkflowAuthFormHeading({
  workflowTitle,
  authRoute,
}: {
  workflowTitle: string;
  authRoute: "signup" | "login";
}) {
  const isSignup = authRoute === "signup";
  const description = isSignup
    ? "This workflow opens once your account is ready."
    : "Sign in to open this workflow.";

  return (
    <div className="mb-6 flex flex-col gap-1.5">
      <div className="flex min-w-0 flex-col gap-1.5">
        <h1 className="text-xl font-semibold leading-snug text-default">
          {isSignup ? "Create your account" : "Sign in"}
        </h1>
        <p className="text-sm leading-5 text-muted">{description}</p>
        <span className="text-sm text-muted lg:sr-only">{workflowTitle}</span>
      </div>
    </div>
  );
}

/** What the workflow a marketing link names does, in the app's own words: its title, description and steps. */
export function WorkflowAuthPreview({
  artworkId,
  workflow,
}: {
  artworkId: UseCaseArtworkId;
  workflow: Workflow;
}) {
  return (
    <div className="relative size-full overflow-hidden">
      <div aria-hidden="true" className="absolute inset-0 bg-brand-seashell" />
      <div
        aria-hidden="true"
        className="absolute inset-0 bg-cover bg-center opacity-80"
        style={{ backgroundImage: `url(${softWaves})` }}
      />
      <div className="relative z-10 flex size-full items-center justify-center p-6">
        <div
          ref={(element) => {
            element?.setAttribute("inert", "");
          }}
          aria-hidden="true"
          className={cn(
            GLASS_PANEL_CLASS,
            "pointer-events-none mx-auto flex max-h-full w-full max-w-2xl select-none flex-col overflow-y-auto rounded-3xl bg-sheet/95 p-2 text-left text-default",
          )}
        >
          <header className="flex min-w-0 flex-col items-center gap-4 px-5 py-4">
            {/* The frame Home's workflow cards draw the same art in, wallpaper included. */}
            <div className="workflow-art relative flex aspect-[3/2] max-h-[34dvh] w-full shrink-0 items-center justify-center overflow-hidden rounded-2xl">
              <UseCaseWorkflowArt slug={artworkId} />
            </div>
            <div className="flex w-full min-w-0 max-w-lg flex-col gap-1 text-center">
              <h2 className="break-words text-xl font-semibold leading-tight text-default">
                {workflow.title}
              </h2>
              <p className="break-words text-sm leading-6 text-muted">
                {workflow.description}
              </p>
            </div>
          </header>
          <ol className="mt-2 flex min-w-0 flex-col border-t-0.5 border-border px-5 pb-3 pt-2">
            {workflow.steps.map((step, index) => {
              return (
                <li
                  key={step.slug}
                  className="flex min-w-0 items-start gap-4 border-t-0.5 border-border py-2 first:border-t-0"
                >
                  <span
                    className={cn(
                      GLASS_PANEL_CLASS,
                      "flex size-8 shrink-0 items-center justify-center rounded-full text-sm leading-none tabular-nums text-muted",
                    )}
                  >
                    {index + 1}
                  </span>
                  <span className="min-w-0 flex-1 py-1.5 text-base font-medium leading-5 text-default [overflow-wrap:anywhere]">
                    {step.title}
                  </span>
                </li>
              );
            })}
          </ol>
        </div>
      </div>
    </div>
  );
}
